import {Capacitor} from '@capacitor/core';
import {App} from '@capacitor/app';
import http from '@/axios';
import {Settings} from '@/constants/settings';
import {Lang} from '@/store/player/types';
import {
    NativeBootstrapContext,
    RoutingBootstrapRequest,
    RoutingBootstrapResponse,
    RoutingPlatform,
    ServerType,
} from '@/model/routing';
import {Http, STORAGE_AUTHORIZATION} from '@/constants/http';
import {StandUrls} from '@/utils/stand-urls';

const STORAGE_SERVER_TYPE = 'aof_routing_server_type';
const STORAGE_SERVER_LOCKED = 'aof_routing_server_locked';
const STORAGE_API_BASE_URL = 'aof_routing_api_base_url';
const STORAGE_WS_BASE_URL = 'aof_routing_ws_base_url';
const STORAGE_FRONT_BASE_URL = 'aof_routing_front_base_url';
/** Full bootstrap snapshot (incl. peer URLs) — survives reload when lastResponse is empty. */
const STORAGE_ROUTING_SNAPSHOT = 'aof_routing_snapshot';

/**
 * Mobile routing bootstrap (AOF-2936).
 * Native context comes from AOF-2941 / AOF-2943 via window.__AOF_NATIVE_BOOTSTRAP__.
 */
export class RoutingBootstrapService {

    private static lastResponse: RoutingBootstrapResponse | undefined;
    private static bootstrapped = false;

    public static isMobileApp(): boolean {
        if (process.env.VUE_APP_IS_MOBILE_APP === 'true') {
            return true;
        }
        // Native iOS/Android WebView may load the web stand build (IS_MOBILE_APP=false)
        // but still inject __AOF_NATIVE_BOOTSTRAP__ (AOF-2943). Treat that as mobile
        // so dual-login / JS bootstrap run inside the shell.
        const native = typeof window !== 'undefined' ? window.__AOF_NATIVE_BOOTSTRAP__ : undefined;
        const platform = native?.platform;
        return platform === 'IOS' || platform === 'ANDROID' || platform === 'TEST';
    }

    public static isServerLocked(): boolean {
        if (localStorage.getItem(STORAGE_SERVER_LOCKED) === 'true') {
            return true;
        }
        // After setGameUrl the new origin has empty localStorage — trust native injection.
        const native = typeof window !== 'undefined' ? window.__AOF_NATIVE_BOOTSTRAP__ : undefined;
        return !!(native?.locked || native?.routingReason === 'ACCOUNT_LOCATOR');
    }

    public static lockServer(): void {
        localStorage.setItem(STORAGE_SERVER_LOCKED, 'true');
    }

    public static unlockServer(): void {
        localStorage.removeItem(STORAGE_SERVER_LOCKED);
    }

    /** Persist full routing row so dual-login peer URLs survive WebView reload / origin switch. */
    private static persistSnapshot(response: RoutingBootstrapResponse): void {
        try {
            localStorage.setItem(STORAGE_ROUTING_SNAPSHOT, JSON.stringify(response));
            localStorage.setItem(STORAGE_SERVER_TYPE, response.serverType);
            localStorage.setItem(STORAGE_API_BASE_URL, response.apiBaseUrl);
            localStorage.setItem(STORAGE_WS_BASE_URL, response.wsBaseUrl);
            if (response.frontBaseUrl) {
                localStorage.setItem(STORAGE_FRONT_BASE_URL, response.frontBaseUrl);
            }
        } catch (e) {
            // ignore quota / private mode
        }
    }

    private static restoreSnapshot(): RoutingBootstrapResponse | undefined {
        try {
            const raw = localStorage.getItem(STORAGE_ROUTING_SNAPSHOT);
            if (!raw) {
                return undefined;
            }
            const parsed = JSON.parse(raw) as RoutingBootstrapResponse;
            if (!parsed?.serverType || !parsed?.apiBaseUrl) {
                return undefined;
            }
            return RoutingBootstrapService.rewriteStandResponse({
                ...parsed,
                apiBaseUrl: RoutingBootstrapService.normalizeBaseUrl(parsed.apiBaseUrl),
                wsBaseUrl: RoutingBootstrapService.normalizeBaseUrl(
                    parsed.wsBaseUrl || `${parsed.apiBaseUrl}/api/websocket`,
                ),
                frontBaseUrl: RoutingBootstrapService.normalizeBaseUrl(
                    parsed.frontBaseUrl || parsed.apiBaseUrl,
                ),
                peerApiBaseUrl: parsed.peerApiBaseUrl
                    ? RoutingBootstrapService.normalizeBaseUrl(parsed.peerApiBaseUrl)
                    : undefined,
                peerWsBaseUrl: parsed.peerWsBaseUrl
                    ? RoutingBootstrapService.normalizeBaseUrl(parsed.peerWsBaseUrl)
                    : undefined,
                peerFrontBaseUrl: parsed.peerFrontBaseUrl
                    ? RoutingBootstrapService.normalizeBaseUrl(parsed.peerFrontBaseUrl)
                    : undefined,
            });
        } catch (e) {
            return undefined;
        }
    }

    public static getCachedServerType(): ServerType | undefined {
        const value = localStorage.getItem(STORAGE_SERVER_TYPE);
        if (value === 'RU' || value === 'FOREIGN') {
            return value;
        }
        return undefined;
    }

    public static getApiBaseUrl(): string | undefined {
        return RoutingBootstrapService.lastResponse?.apiBaseUrl
            || localStorage.getItem(STORAGE_API_BASE_URL)
            || undefined;
    }

    public static getWsBaseUrl(): string | undefined {
        return RoutingBootstrapService.lastResponse?.wsBaseUrl
            || localStorage.getItem(STORAGE_WS_BASE_URL)
            || undefined;
    }

    public static getLastResponse(): RoutingBootstrapResponse | undefined {
        return RoutingBootstrapService.lastResponse;
    }

    /**
     * Wait briefly for native wrappers to inject context (AOF-2941 / AOF-2943).
     */
    public static async waitForNativeContext(timeoutMs: number = 400): Promise<NativeBootstrapContext | undefined> {
        const ready = (ctx?: NativeBootstrapContext) =>
            !!(ctx && (ctx.platform || ctx.deviceLocale || ctx.storeCountry || ctx.authExternalId
                || ctx.locked || ctx.serverType || ctx.routingReason === 'ACCOUNT_LOCATOR'));
        const existing = window.__AOF_NATIVE_BOOTSTRAP__;
        if (ready(existing)) {
            return existing;
        }
        if (timeoutMs <= 0) {
            return existing;
        }
        const started = Date.now();
        return new Promise(resolve => {
            const timer = setInterval(() => {
                const ctx = window.__AOF_NATIVE_BOOTSTRAP__;
                if (ready(ctx)) {
                    clearInterval(timer);
                    resolve(ctx);
                    return;
                }
                if (Date.now() - started >= timeoutMs) {
                    clearInterval(timer);
                    resolve(ctx);
                }
            }, 50);
        });
    }

    public static async collectRequest(overrideLocale?: string): Promise<RoutingBootstrapRequest> {
        const native = await RoutingBootstrapService.waitForNativeContext();
        const platform = RoutingBootstrapService.resolvePlatform(native);
        let appVersion = native?.appVersion;
        if (!appVersion) {
            try {
                if (Capacitor.isNativePlatform()) {
                    appVersion = (await App.getInfo()).version;
                }
            } catch (e) {
                // ignore — optional field
            }
        }
        if (!appVersion) {
            appVersion = process.env.VUE_APP_ANDROID_VERSION_CODE || undefined;
        }

        const deviceLocale = overrideLocale
            || native?.deviceLocale
            || (typeof navigator !== 'undefined' ? navigator.language : undefined);

        return {
            platform,
            deviceLocale,
            storeCountry: native?.storeCountry,
            appVersion,
            authProvider: native?.authProvider,
            authExternalId: native?.authExternalId,
        };
    }

    /**
     * Call RoutingService and apply api/ws/lang. No-op on non-mobile.
     * If server is locked, only re-apply cached URLs (no locale re-route).
     */
    public static async bootstrap(options?: { force?: boolean; deviceLocale?: string }): Promise<RoutingBootstrapResponse | undefined> {
        if (!RoutingBootstrapService.isMobileApp()) {
            return undefined;
        }

        // Native lock after dual-login (new origin has empty localStorage).
        if (!options?.force && RoutingBootstrapService.applyNativeLockIfPresent()) {
            RoutingBootstrapService.bootstrapped = true;
            return RoutingBootstrapService.lastResponse;
        }

        if (RoutingBootstrapService.isServerLocked() && !options?.force) {
            RoutingBootstrapService.applyCachedUrls();
            if (!RoutingBootstrapService.lastResponse?.peerApiBaseUrl) {
                RoutingBootstrapService.seedFromNativeContext();
            }
            return RoutingBootstrapService.lastResponse;
        }

        // Seed from native injection first (includes peer URLs for dual-login).
        RoutingBootstrapService.seedFromNativeContext();

        const request = await RoutingBootstrapService.collectRequest(options?.deviceLocale);
        // Seed host = build-time VUE_APP_API_HOST (both backends share routing config).
        const response = await http.post<RoutingBootstrapResponse>('/api/routing/bootstrap', request)
            .then(r => r.data);

        RoutingBootstrapService.applyResponse(response);
        RoutingBootstrapService.bootstrapped = true;
        return response;
    }

    /**
     * After setGameUrl the WebView opens peer front with empty localStorage.
     * Honor native locked shard so we do NOT re-bootstrap by deviceLocale (en-US → FOREIGN).
     */
    private static applyNativeLockIfPresent(): boolean {
        const native = typeof window !== 'undefined' ? window.__AOF_NATIVE_BOOTSTRAP__ : undefined;
        if (!native?.serverType || !native.apiBaseUrl) {
            return false;
        }
        if (!(native.locked || native.routingReason === 'ACCOUNT_LOCATOR')) {
            return false;
        }
        RoutingBootstrapService.seedFromNativeContext();
        if (!RoutingBootstrapService.lastResponse) {
            return false;
        }
        RoutingBootstrapService.lockServer();
        RoutingBootstrapService.persistSnapshot(RoutingBootstrapService.lastResponse);
        if (RoutingBootstrapService.lastResponse.defaultLang === 'EN') {
            Settings.setEn();
        } else {
            Settings.setRu();
        }
        StandUrls.debugLog(
            `Routing native lock applied: serverType=${RoutingBootstrapService.lastResponse.serverType}`
            + `, api=${RoutingBootstrapService.lastResponse.apiBaseUrl}`
            + `, peer=${RoutingBootstrapService.lastResponse.peerApiBaseUrl || '-'}`,
        );
        return true;
    }

    /** Apply native-resolved routing (with peer URLs) before / instead of waiting on JS POST. */
    private static seedFromNativeContext(): void {
        const native = typeof window !== 'undefined' ? window.__AOF_NATIVE_BOOTSTRAP__ : undefined;
        if (!native?.serverType || !native.apiBaseUrl) {
            return;
        }
        const apiBaseUrl = RoutingBootstrapService.normalizeBaseUrl(native.apiBaseUrl);
        const wsBaseUrl = RoutingBootstrapService.normalizeBaseUrl(
            native.wsBaseUrl || `${apiBaseUrl}/api/websocket`,
        );
        const frontRaw = native.frontBaseUrl || apiBaseUrl;
        // front may contain ?Authorization= from setGameUrl — keep API host clean.
        let frontBaseUrl = RoutingBootstrapService.normalizeBaseUrl(frontRaw);
        try {
            const u = new URL(frontRaw, window.location.href);
            u.searchParams.delete('Authorization');
            frontBaseUrl = RoutingBootstrapService.normalizeBaseUrl(u.origin + u.pathname);
        } catch (e) {
            // keep normalized frontRaw
        }
        RoutingBootstrapService.lastResponse = RoutingBootstrapService.rewriteStandResponse({
            serverType: native.serverType,
            apiBaseUrl,
            wsBaseUrl,
            frontBaseUrl,
            peerServerType: native.peerServerType,
            peerApiBaseUrl: native.peerApiBaseUrl
                ? RoutingBootstrapService.normalizeBaseUrl(native.peerApiBaseUrl)
                : undefined,
            peerWsBaseUrl: native.peerWsBaseUrl
                ? RoutingBootstrapService.normalizeBaseUrl(native.peerWsBaseUrl)
                : undefined,
            peerFrontBaseUrl: native.peerFrontBaseUrl
                ? RoutingBootstrapService.normalizeBaseUrl(native.peerFrontBaseUrl)
                : undefined,
            defaultLang: native.defaultLang || (native.serverType === 'RU' ? 'RU' : 'EN'),
            supportedLangs: ['RU', 'EN'],
            paymentRegion: native.paymentRegion || 'FOREIGN',
            routingReason: native.routingReason || 'DEVICE_LOCALE',
        });
        http.defaults.baseURL = RoutingBootstrapService.lastResponse.apiBaseUrl;
    }

    /**
     * Re-run routing after UI language change before character select / lock.
     */
    public static async rerouteForLanguage(lang: Lang): Promise<void> {
        if (!RoutingBootstrapService.isMobileApp() || RoutingBootstrapService.isServerLocked()) {
            return;
        }
        const locale = lang === Lang.RU ? 'ru' : 'en';
        await RoutingBootstrapService.bootstrap({deviceLocale: locale});
    }

    /**
     * After dual-login / explicit server switch: pin URLs and lock.
     */
    public static applyAndLock(serverType: ServerType, apiBaseUrl: string, wsBaseUrl?: string): void {
        const prev = RoutingBootstrapService.lastResponse;
        const normalizedApi = RoutingBootstrapService.normalizeBaseUrl(apiBaseUrl);
        const normalizedWs = wsBaseUrl
            ? RoutingBootstrapService.normalizeBaseUrl(wsBaseUrl)
            : `${normalizedApi}/api/websocket`;
        const peerServerType: ServerType = serverType === 'RU' ? 'FOREIGN' : 'RU';
        const peerApiBaseUrl = prev?.serverType === serverType
            ? (prev.peerApiBaseUrl || prev.apiBaseUrl)
            : (prev?.apiBaseUrl || normalizedApi);
        const peerWsBaseUrl = prev?.serverType === serverType
            ? (prev.peerWsBaseUrl || prev.wsBaseUrl)
            : (prev?.wsBaseUrl || `${peerApiBaseUrl}/api/websocket`);
        const peerFrontBaseUrl = prev?.serverType === serverType
            ? (prev.peerFrontBaseUrl || prev.frontBaseUrl)
            : (prev?.frontBaseUrl || peerApiBaseUrl);
        const frontBaseUrl = prev?.serverType === serverType
            ? (prev.frontBaseUrl || normalizedApi)
            : (prev?.peerFrontBaseUrl || normalizedApi);

        const normalizedFront = RoutingBootstrapService.normalizeBaseUrl(frontBaseUrl || normalizedApi);
        RoutingBootstrapService.lockServer();
        RoutingBootstrapService.lastResponse = RoutingBootstrapService.rewriteStandResponse({
            serverType,
            apiBaseUrl: normalizedApi,
            wsBaseUrl: normalizedWs,
            frontBaseUrl: normalizedFront,
            peerServerType,
            peerApiBaseUrl: RoutingBootstrapService.normalizeBaseUrl(peerApiBaseUrl || ''),
            peerWsBaseUrl: RoutingBootstrapService.normalizeBaseUrl(peerWsBaseUrl || ''),
            peerFrontBaseUrl: RoutingBootstrapService.normalizeBaseUrl(peerFrontBaseUrl || ''),
            defaultLang: serverType === 'RU' ? 'RU' : 'EN',
            supportedLangs: ['RU', 'EN'],
            paymentRegion: prev?.paymentRegion || 'FOREIGN',
            routingReason: 'ACCOUNT_LOCATOR',
        });
        http.defaults.baseURL = RoutingBootstrapService.lastResponse.apiBaseUrl;
        RoutingBootstrapService.persistSnapshot(RoutingBootstrapService.lastResponse);
    }

    /**
     * After login / dual-login: switch WebView to locked front (first-party cookie)
     * or reload same origin. Prefer native setGameUrl when available.
     * Passes JWT in query — localStorage is per-origin and does not survive lgtest→zazer.
     */
    public static navigateAfterAuth(): void {
        const front = RoutingBootstrapService.lastResponse?.frontBaseUrl
            || localStorage.getItem(STORAGE_FRONT_BASE_URL)
            || '';
        const api = RoutingBootstrapService.getApiBaseUrl() || '';
        const ws = RoutingBootstrapService.getWsBaseUrl() || '';
        const serverType = RoutingBootstrapService.getCachedServerType() || '';
        const authorization = Http.AUTHORIZATION
            || (typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_AUTHORIZATION) : null)
            || '';

        const frontWithAuth = RoutingBootstrapService.appendAuthorization(front, authorization || undefined);

        const payload = {
            frontBaseUrl: front,
            apiBaseUrl: api,
            wsBaseUrl: ws,
            serverType,
            authorization: authorization || undefined,
            locked: true,
            peerServerType: RoutingBootstrapService.lastResponse?.peerServerType,
            peerApiBaseUrl: RoutingBootstrapService.lastResponse?.peerApiBaseUrl,
            peerWsBaseUrl: RoutingBootstrapService.lastResponse?.peerWsBaseUrl,
            peerFrontBaseUrl: RoutingBootstrapService.lastResponse?.peerFrontBaseUrl,
        };
        const w = window as any;
        if (w.webkit?.messageHandlers?.setGameUrl) {
            StandUrls.debugLog(`[Routing] notify native setGameUrl front=${front} api=${api} serverType=${serverType} auth=${authorization ? 'yes' : 'no'}`);
            w.webkit.messageHandlers.setGameUrl.postMessage(payload);
            return;
        }
        // Android Play WebView: window.app.setGameUrl(JSON string)
        if (typeof w.app?.setGameUrl === 'function') {
            StandUrls.debugLog(`[Routing] notify Android setGameUrl front=${front} api=${api} serverType=${serverType} auth=${authorization ? 'yes' : 'no'}`);
            w.app.setGameUrl(JSON.stringify(payload));
            return;
        }

        if (frontWithAuth) {
            try {
                const target = new URL(frontWithAuth, window.location.href);
                if (target.host && target.host !== window.location.host) {
                    StandUrls.debugLog(`[Routing] location.replace peer front ${target.origin}${target.pathname}`);
                    window.location.replace(target.href);
                    return;
                }
            } catch (e) {
                // fall through to reload
            }
        }
        window.location.reload();
    }

    /**
     * OWN logout: drop shard lock so next login can dual-login to the other server again.
     */
    public static unlockAfterLogout(): void {
        RoutingBootstrapService.unlockServer();
        try {
            localStorage.removeItem(STORAGE_ROUTING_SNAPSHOT);
            localStorage.removeItem(STORAGE_SERVER_TYPE);
            localStorage.removeItem(STORAGE_API_BASE_URL);
            localStorage.removeItem(STORAGE_WS_BASE_URL);
            localStorage.removeItem(STORAGE_FRONT_BASE_URL);
            localStorage.removeItem(STORAGE_AUTHORIZATION);
        } catch (e) {
            // ignore
        }
        Http.AUTHORIZATION = '';
        RoutingBootstrapService.lastResponse = undefined;

        const unlockPayload = {action: 'unlock', locked: 0};
        const w = window as any;
        if (w.webkit?.messageHandlers?.setGameUrl) {
            StandUrls.debugLog('[Routing] notify native unlock after logout');
            // Use action + numeric 0: WK bridge often coerces bool false in ways Swift misses.
            // Native clears site data + force-reloads front (no JS fallback — it raced with setGameUrl).
            w.webkit.messageHandlers.setGameUrl.postMessage(unlockPayload);
            return;
        }
        if (typeof w.app?.setGameUrl === 'function') {
            StandUrls.debugLog('[Routing] notify Android unlock after logout');
            w.app.setGameUrl(JSON.stringify(unlockPayload));
            return;
        }
        window.location.reload();
    }

    private static appendAuthorization(front: string, authorization?: string): string {
        if (!front || !authorization) {
            return front;
        }
        try {
            const u = new URL(front, window.location.href);
            u.searchParams.set('Authorization', authorization);
            return u.href;
        } catch (e) {
            return front;
        }
    }

    public static normalizePublicBaseUrl(url: string): string {
        return RoutingBootstrapService.normalizeBaseUrl(url);
    }

    private static rewriteStandResponse(response: RoutingBootstrapResponse): RoutingBootstrapResponse {
        return {
            ...response,
            apiBaseUrl: StandUrls.rewrite(response.apiBaseUrl),
            wsBaseUrl: StandUrls.rewrite(response.wsBaseUrl),
            frontBaseUrl: StandUrls.rewrite(response.frontBaseUrl),
            peerApiBaseUrl: response.peerApiBaseUrl
                ? StandUrls.rewrite(response.peerApiBaseUrl)
                : undefined,
            peerWsBaseUrl: response.peerWsBaseUrl
                ? StandUrls.rewrite(response.peerWsBaseUrl)
                : undefined,
            peerFrontBaseUrl: response.peerFrontBaseUrl
                ? StandUrls.rewrite(response.peerFrontBaseUrl)
                : undefined,
        };
    }

    private static applyResponse(response: RoutingBootstrapResponse): void {
        const rewritten = RoutingBootstrapService.rewriteStandResponse({
            ...response,
            apiBaseUrl: RoutingBootstrapService.normalizeBaseUrl(response.apiBaseUrl),
            wsBaseUrl: RoutingBootstrapService.normalizeBaseUrl(response.wsBaseUrl),
            frontBaseUrl: RoutingBootstrapService.normalizeBaseUrl(
                response.frontBaseUrl || response.apiBaseUrl,
            ),
            peerApiBaseUrl: response.peerApiBaseUrl
                ? RoutingBootstrapService.normalizeBaseUrl(response.peerApiBaseUrl)
                : undefined,
            peerWsBaseUrl: response.peerWsBaseUrl
                ? RoutingBootstrapService.normalizeBaseUrl(response.peerWsBaseUrl)
                : undefined,
            peerFrontBaseUrl: response.peerFrontBaseUrl
                ? RoutingBootstrapService.normalizeBaseUrl(response.peerFrontBaseUrl)
                : undefined,
        });
        http.defaults.baseURL = rewritten.apiBaseUrl;
        RoutingBootstrapService.lastResponse = rewritten;
        RoutingBootstrapService.persistSnapshot(rewritten);

        if (response.defaultLang === 'EN') {
            Settings.setEn();
        } else {
            Settings.setRu();
        }

        StandUrls.debugLog(
            `Routing bootstrap: serverType=${rewritten.serverType}, api=${rewritten.apiBaseUrl}, reason=${rewritten.routingReason}`,
        );
    }

    private static applyCachedUrls(): void {
        const snap = RoutingBootstrapService.restoreSnapshot();
        if (snap) {
            RoutingBootstrapService.lastResponse = snap;
            http.defaults.baseURL = snap.apiBaseUrl;
            if (snap.defaultLang === 'EN') {
                Settings.setEn();
            } else if (snap.defaultLang === 'RU') {
                Settings.setRu();
            }
            StandUrls.debugLog(
                `Routing restore snapshot: serverType=${snap.serverType}, api=${snap.apiBaseUrl}, peer=${snap.peerApiBaseUrl || '-'}`,
            );
            return;
        }
        const api = localStorage.getItem(STORAGE_API_BASE_URL);
        if (api) {
            http.defaults.baseURL = api;
        }
    }

    private static resolvePlatform(native?: NativeBootstrapContext): RoutingPlatform {
        if (native?.platform) {
            return native.platform;
        }
        const cap = Capacitor.getPlatform();
        if (cap === 'ios') {
            return 'IOS';
        }
        if (cap === 'android') {
            return 'ANDROID';
        }
        // Capacitor web / plain WebView wrappers without injected context.
        if (process.env.VUE_APP_IS_MOBILE_APP === 'true') {
            const type = (process.env.VUE_APP_MOBILE_APP_TYPE || '').toUpperCase();
            if (type.includes('DEV') || type.includes('TEST')) {
                return 'TEST';
            }
            // Prefer ANDROID as default for Play WebView until AOF-2941 injects platform.
            return 'ANDROID';
        }
        return 'WEB';
    }

    private static normalizeBaseUrl(url: string): string {
        if (!url) {
            return '';
        }
        let trimmed = url.trim();
        while (trimmed.endsWith('/')) {
            trimmed = trimmed.substring(0, trimmed.length - 1);
        }
        return trimmed;
    }
}
